import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import useLocationSearchCached from "../../../hooks/useLocationSearchCached";
import GeocodingCache from "../../../utils/geocodingCache";
import { geocodingMonitor } from "../../../utils/geocodingMonitor";

declare global {
  interface Window {
    google: any;
  }
}

interface StoreFinderProps {
  layout?: "default" | "store-locator";
  variantIds?: string[];
  onStoresFetched?: (stores: any[]) => void;
  onSearch?: (query: string) => void;
}

const StoreFinder = forwardRef<any, StoreFinderProps>(({
  layout = "default",
  variantIds = [],
  onStoresFetched = (_stores: any[]) => {},
  onSearch,
}, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const [query, setQuery] = useState("");
  const [predictions, setPredictions] = useState<any[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const [userCoords, setUserCoords] = useState(null);
  const [hasManualSearch, setHasManualSearch] = useState(false);

  const {
    handleLocationSearch,
    searchByQuery,
    stores,
    loading,
    userCoords: locUserCoords,
    hasManualSearch: locHasManualSearch,
    errorMessage,
  } = useLocationSearchCached(inputRef, variantIds);

  // Cached autocomplete predictions to reduce API calls
  const autocompleteCache = useRef(new Map());
  const AUTOCOMPLETE_CACHE_EXPIRY = 60 * 60 * 1000; // 1 hour - balances performance

  const autocompleteServiceRef = useRef<any>(null);
  const placesServiceRef = useRef<any>(null);
  const sessionTokenRef = useRef<any>(null);

  const getCachedAutocomplete = (query) => {
    const cached = autocompleteCache.current.get(query);
    if (cached && Date.now() - cached.timestamp < AUTOCOMPLETE_CACHE_EXPIRY) {
      GeocodingCache.trackStats('hit', 'memory', 'autocomplete');
      return cached.results;
    }
    GeocodingCache.trackStats('miss', null, 'autocomplete');
    return null;
  };

  const setCachedAutocomplete = (query, results) => {
    autocompleteCache.current.set(query, {
      results,
      timestamp: Date.now()
    });
    
    // Limit cache size
    if (autocompleteCache.current.size > 50) {
      const firstKey = autocompleteCache.current.keys().next().value;
      autocompleteCache.current.delete(firstKey);
    }
  };

  // Cached geocoding function using Google Geocoding API
  const geocodeWithGoogleAPI = async (query: string) => {
    // Try to get API key from Shopify theme settings first
    let API_KEY = (window as any)?.theme?.settings?.google_maps_api_key;
    
    // Fallback to environment variable
    if (!API_KEY) {
      API_KEY = process.env.REACT_APP_GOOGLE_GEOCODING_API_KEY;
    }
    
    if (!API_KEY) {
      console.warn('[StoreFinder] Google Geocoding API key not configured in theme settings or environment');
      // Fallback to Places API if available
      throw new Error('Google Geocoding API key not configured');
    }

    // Determine if this is a reverse geocoding request (lat,lng format)
    const isReverseGeocode = /^-?\d+\.?\d*,-?\d+\.?\d*$/.test(query.trim());
    
    const params = new URLSearchParams({
      [isReverseGeocode ? 'latlng' : 'address']: query,
      components: 'country:AU',
      key: API_KEY
    });

    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?${params}`
    );

    if (!response.ok) {
      throw new Error(`Geocoding API error: ${response.status}`);
    }

    const data = await response.json();

    if (data.status !== 'OK' || !data.results || data.results.length === 0) {
      throw new Error(`No results found for: ${query}`);
    }

    const result = data.results[0];
    
    // Extract components
    const components: any = {};
    result.address_components.forEach((component: any) => {
      component.types.forEach((type: string) => {
        components[type] = component.long_name;
      });
    });

    // Prefer locality, fall back to postal_town or sublocality_level_1
    const locality = components.locality || 
      components.postal_town || 
      components.sublocality_level_1 || 
      '';

    return {
      formattedAddress: result.formatted_address,
      location: result.geometry?.location ? {
        lat: result.geometry.location.lat,
        lng: result.geometry.location.lng
      } : null,
      components: {
        postal_code: components.postal_code || '',
        locality: locality,
        administrative_area_level_1: components.administrative_area_level_1 || '',
        country: components.country || 'AU'
      }
    };
  };

  useImperativeHandle(ref, () => ({
    searchStores: () => {
      if (query.trim()) {
        searchByQuery(query.trim());
        onSearch?.(query.trim());
      }
    }
  }));

  // Notify parent when store list updates
  useEffect(() => {
    if (stores && stores.length > 0) {
      console.log(`[StoreFinder] Store search returned ${stores.length} stores:`, stores.map(s => s.title || s.name));
    } else if (stores) {
      console.log('[StoreFinder] Store search returned 0 stores');
    }
    onStoresFetched(stores);
  }, [stores]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // console.log(`[StoreFinder] Input changed: "${value}"`);
    setQuery(value);

    // Handle autocomplete predictions based on character length
    const trimmed = value.trim();
    const MIN_CHARS = 4;

    if (trimmed.length >= MIN_CHARS) {
      // Debounce both search and autocomplete predictions
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      debounceTimeout.current = setTimeout(() => {
        // Handle autocomplete predictions
        if (window.google?.maps && autocompleteServiceRef.current) {
          // Check cache first
          const cachedPredictions = getCachedAutocomplete(trimmed);
          if (cachedPredictions) {
            console.log('[StoreFinder] Using cached autocomplete predictions');
            setPredictions(cachedPredictions);
            setShowDropdown(true);
            setSelectedIndex(-1);
          } else {
            // console.log(`[Autocomplete] Requesting predictions for: "${trimmed}"`);
            
            autocompleteServiceRef.current.getPlacePredictions(
              {
                input: trimmed,
                types: ['geocode'],
                componentRestrictions: { country: 'AU' },
                sessionToken: sessionTokenRef.current,
                bounds: new window.google.maps.LatLngBounds(
                  new window.google.maps.LatLng(-44.0, 113.0),
                  new window.google.maps.LatLng(-10.0, 154.0)
                )
              },
              (results: any[], status: any) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
                  // console.log(`[Autocomplete] Received ${results.length} predictions`);
                  setCachedAutocomplete(trimmed, results);
                  setPredictions(results);
                  setShowDropdown(true);
                  setSelectedIndex(-1);
                } else {
                  setPredictions([]);
                  setShowDropdown(false);
                }
              }
            );
          }
        }

        // Let the cached hook handle ALL geocoding - no manual geocoding here
        if (layout === "store-locator") {
          searchByQuery(trimmed, true);
        } else {
          searchByQuery(trimmed);
        }
        onSearch?.(trimmed);
      }, 500);
    } else {
      // Clear predictions and dropdown for short queries
      setPredictions([]);
      setShowDropdown(false);
      
      // Handle empty search
      if (trimmed === "") {
        if (debounceTimeout.current) {
          clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
          // Use cached geocoding for empty search too
          if (layout === "store-locator") {
            searchByQuery(trimmed, true);
          } else {
            searchByQuery(trimmed);
          }
          onSearch?.(trimmed);
        }, 300);
      }
    }
  };

  // Handle keyboard navigation for dropdown
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || predictions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, predictions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && predictions[selectedIndex]) {
          selectPrediction(predictions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setPredictions([]);
        setSelectedIndex(-1);
        break;
    }
  };

  // Function to select a prediction with caching
  const selectPrediction = async (prediction: any) => {
    console.log(`[StoreFinder] Selecting: ${prediction.description}`);
    
    // First check if we have this location cached
    const cachedResult = GeocodingCache.lookupGeocodeCache(prediction.description);
    
    if (cachedResult) {
      console.log('[StoreFinder] Using cached prediction result');
      const formatted = cachedResult.formattedAddress;
      
      // Update input and state
      if (inputRef.current) {
        inputRef.current.value = formatted;
      }
      setQuery(formatted);
      onSearch?.(formatted);
      
      // Skip searchByQuery - directly use coordinates if available
      if (cachedResult.location) {
        setUserCoords({ lat: cachedResult.location.lat, lng: cachedResult.location.lng });
        setHasManualSearch(true);
        console.log('[StoreFinder] Using cached coordinates, skipping geocoding');
        // We could call fetchStores directly here, but let searchByQuery handle it with triggeredByAutocomplete=true
        if (layout === "store-locator") {
          searchByQuery(formatted, true);
        } else {
          searchByQuery(formatted, true); // Set to true to skip store fetching in useLocationSearchCached
        }
      } else {
        // No coordinates in cache, need to geocode
        if (layout === "store-locator") {
          searchByQuery(formatted, true);
        } else {
          searchByQuery(formatted);
        }
      }

      // Hide dropdown
      setShowDropdown(false);
      setPredictions([]);
      return;
    }
    
    // If not cached, proceed with Places API and cache the result
    if (placesServiceRef.current && sessionTokenRef.current) {
      GeocodingCache.trackStats('api_call', null, 'places');
      placesServiceRef.current.getDetails(
        {
          placeId: prediction.place_id,
          fields: ['geometry', 'formatted_address', 'name', 'address_components'],
          sessionToken: sessionTokenRef.current
        },
        (place: any, status: any) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry) {
            const formatted = place.formatted_address || place.name;
            
            // Create normalized geocode result for caching
            const components: any = {};
            if (place.address_components) {
              place.address_components.forEach((component: any) => {
                component.types.forEach((type: string) => {
                  components[type] = component.long_name;
                });
              });
            }

            const locality = components.locality || 
              components.postal_town || 
              components.sublocality_level_1 || 
              '';

            const normalizedResult = {
              formattedAddress: formatted,
              location: place.geometry?.location ? {
                lat: typeof place.geometry.location.lat === 'function' 
                  ? place.geometry.location.lat() 
                  : place.geometry.location.lat,
                lng: typeof place.geometry.location.lng === 'function' 
                  ? place.geometry.location.lng() 
                  : place.geometry.location.lng
              } : null,
              components: {
                postal_code: components.postal_code || '',
                locality: locality,
                administrative_area_level_1: components.administrative_area_level_1 || '',
                country: components.country || 'AU'
              }
            };

            // Store in cache for future use
            GeocodingCache.storeGeocodeResult(prediction.description, normalizedResult);
            
            // Update input and state
            if (inputRef.current) {
              inputRef.current.value = formatted;
            }
            setQuery(formatted);
            onSearch?.(formatted);
            
            // We have coordinates from Places API - set them and skip geocoding
            if (normalizedResult.location) {
              setUserCoords({ 
                lat: normalizedResult.location.lat, 
                lng: normalizedResult.location.lng 
              });
              setHasManualSearch(true);
              console.log('[StoreFinder] Using Places API coordinates, skipping geocoding');
              // Use triggeredByAutocomplete=true to skip geocoding in the hook
              if (layout === "store-locator") {
                searchByQuery(formatted, true);
              } else {
                searchByQuery(formatted, true);
              }
            } else {
              // No coordinates available, need to geocode
              if (layout === "store-locator") {
                searchByQuery(formatted, true);
              } else {
                searchByQuery(formatted);
              }
            }

            // Create new session token
            sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
            
            // Hide dropdown
            setShowDropdown(false);
            setPredictions([]);
          }
        }
      );
    } else {
      // Fallback: use prediction description directly
      const formatted = prediction.description;
      if (inputRef.current) {
        inputRef.current.value = formatted;
      }
      setQuery(formatted);
      onSearch?.(formatted);
      
      if (layout === "store-locator") {
        searchByQuery(formatted, true);
      } else {
        searchByQuery(formatted);
      }
      
      setShowDropdown(false);
      setPredictions([]);
    }
  };

  useEffect(() => {
    if (!window.google?.maps) return;

    // Initialize monitoring in development
    if (process.env.NODE_ENV === 'development') {
      geocodingMonitor.startRealTimeMonitoring();
    }

    // Initialise services once
    autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    
    const mapDiv = document.createElement('div');
    const map = new window.google.maps.Map(mapDiv);
    placesServiceRef.current = new window.google.maps.places.PlacesService(map);
    
    sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();

    // Hide dropdown when clicking outside
    const handleClickOutside = (e: Event) => {
      if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const handleUseMyLocation = async () => {
    setQuery("");
    if (inputRef.current) inputRef.current.value = "";
    setHasManualSearch(false);
    setUserCoords(null);

    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      const { latitude, longitude } = position.coords;
      
      // Create reverse geocoding key for cache lookup
      const reverseKey = GeocodingCache.createReverseGeocodeKey(latitude, longitude);
      
      try {
        // Try cached geocoding first
        const cachedResult = GeocodingCache.lookupGeocodeCache(reverseKey);
        
        let formatted: string;
        
        if (cachedResult) {
          console.log('[StoreFinder] Using cached reverse geocoding result');
          console.log('[StoreFinder] Cached result details:', {
            formattedAddress: cachedResult.formattedAddress,
            fromCache: cachedResult.fromCache,
            fromCookie: cachedResult.fromCookie,
            components: cachedResult.components
          });
          
          // Check if the cached result is valid (not "Unknown Location")
          if (cachedResult.formattedAddress === 'Unknown Location' || !cachedResult.formattedAddress) {
            console.warn('[StoreFinder] Cached result is invalid, clearing and performing fresh geocoding');
            // Clear the bad cache entry by removing from session storage
            try {
              const cache = JSON.parse(sessionStorage.getItem('mjbale_geocoding_cache_v1') || '{}');
              delete cache[reverseKey];
              sessionStorage.setItem('mjbale_geocoding_cache_v1', JSON.stringify(cache));
            } catch (e) {
              console.warn('[StoreFinder] Failed to clear bad cache entry');
            }
            // Force fresh geocoding by throwing an error to fall through to catch block
            throw new Error('Invalid cached result, forcing fresh geocoding');
          }
          
          formatted = cachedResult.formattedAddress;
        } else {
          // No cache hit, perform fresh geocoding
          throw new Error('No cached result, performing fresh geocoding');
        }

        // If we get here, we have a valid cached result
        console.log(`[StoreFinder] Using location address for search: "${formatted}"`);
        console.log(`[StoreFinder] GPS coordinates: ${latitude}, ${longitude}`);
        
        onSearch?.(formatted);
        searchByQuery(formatted);
        
      } catch (cacheError: any) {
        // Either no cache or invalid cache - perform fresh geocoding
        console.log('[StoreFinder] Performing fresh reverse geocoding:', cacheError.message);
        
        let formatted: string;
        
        try {
          // Try Google Geocoding API with cache
          const geocodeResult = await GeocodingCache.geocodeWithCache(
            reverseKey,
            () => geocodeWithGoogleAPI(`${latitude},${longitude}`)
          );
          
          if (geocodeResult) {
            formatted = geocodeResult.formattedAddress;
            console.log('[StoreFinder] Fresh geocoding result details:', {
              formattedAddress: geocodeResult.formattedAddress,
              fromCache: geocodeResult.fromCache,
              fromCookie: geocodeResult.fromCookie,
              components: geocodeResult.components
            });
          } else {
            throw new Error('No result from geocoding');
          }
        } catch (geocodeError) {
          console.warn('[StoreFinder] Geocoding API failed, falling back to Google Maps Geocoder:', geocodeError);
          
          // Fallback to original Google Maps Geocoder
          if (!window.google?.maps) {
            throw new Error('Google Maps not available');
          }
          
          const latlng = new window.google.maps.LatLng(latitude, longitude);
          const geocoder = new window.google.maps.Geocoder();
          
          const geocoderResult = await new Promise<any>((resolve, reject) => {
            geocoder.geocode({ location: latlng }, (results: any, status: any) => {
              if (status === "OK" && results[0]) {
                resolve(results[0]);
              } else {
                reject(new Error(`Geocoder failed: ${status}`));
              }
            });
          });
          
          formatted = geocoderResult.formatted_address;
          
          // Extract components properly from the geocoder result
          const components: any = {};
          if (geocoderResult.address_components) {
            geocoderResult.address_components.forEach((component: any) => {
              component.types.forEach((type: string) => {
                components[type] = component.long_name;
              });
            });
          }

          const locality = components.locality || 
            components.postal_town || 
            components.sublocality_level_1 || 
            '';
          
          // Store in cache for future use with proper components
          const normalizedResult = {
            formattedAddress: formatted,
            location: { lat: latitude, lng: longitude },
            components: {
              postal_code: components.postal_code || '',
              locality: locality,
              administrative_area_level_1: components.administrative_area_level_1 || '',
              country: components.country || 'AU'
            }
          };
          
          GeocodingCache.storeGeocodeResult(reverseKey, normalizedResult);
        }

        // Use the fresh geocoding result
        console.log(`[StoreFinder] Using location address for search: "${formatted}"`);
        console.log(`[StoreFinder] GPS coordinates: ${latitude}, ${longitude}`);
        
        onSearch?.(formatted);
        searchByQuery(formatted);
      }
        
    } catch (geolocationError) {
      console.error('[StoreFinder] Geolocation failed:', geolocationError);
      alert("Unable to access your location. Please ensure location services are enabled.");
    }
  };

  const handleSearch = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (query.trim()) {
      // Simply use searchByQuery which now uses cached hook internally
      onSearch?.(query.trim());
      if (layout === "store-locator") {
        searchByQuery(query.trim(), true);
      } else {
        searchByQuery(query.trim());
      }
    }
  };

  return (
    <>
      <div
        className={`relative z-[1] flex items-center gap-2 ${
          layout === "store-locator" ? "justify-start flex-col lg:flex-row" : ""
        } mb-6`}
      >
        <div className="relative flex items-center w-full border border-[#00263E]">
          {layout !== "store-locator" && (
            <label className="blabel font-medium absolute px-1 left-3 top-0 transform -translate-y-1/2 bg-white text-[#54565A] z-10">
              Enter Suburb or Postcode
            </label>
          )}
          <input
            id="storeFinderInput"
            ref={inputRef}
            autoComplete="off"
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            className="w-full px-4 py-2 border-none"
            placeholder={layout === "store-locator" ? "Enter Suburb or Postcode" : ""}
          />
          {/* <button
            className="px-4 text-[#00263E] w-auto"
            onClick={handleSearch}
          >
            <i className="icon icon-arrow-right"></i>
          </button> */}

          {/* Manual Autocomplete Dropdown */}
          {showDropdown && predictions.length > 0 && (
            <div className="absolute top-full left-0 right-0 bg-white border border-[#00263E] border-t-0 max-h-60 overflow-y-auto z-50 -mx-[1px]">
              {predictions.map((prediction, index) => (
                <div
                  key={prediction.place_id}
                  className={`px-4 py-3 cursor-pointer hover:bg-gray-100 border-b border-gray-200 last:border-b-0 ${
                    index === selectedIndex ? 'bg-blue-100' : ''
                  }`}
                  onClick={() => selectPrediction(prediction)}
                >
                  <div className="text-sm font-medium text-[#00263E]">
                    {prediction.structured_formatting?.main_text || prediction.description}
                  </div>
                  {prediction.structured_formatting?.secondary_text && (
                    <div className="text-xs text-gray-600">
                      {prediction.structured_formatting.secondary_text}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <span className="bc text-[#54565A]">OR</span>

        <button
          className={`${
            layout === "store-locator" ? "px-4 py-3" : "w-[50px] p-0"
          } bg-[#00263E] text-white flex flex-none items-center gap-2`}
          onClick={handleUseMyLocation}
        >
          <i className="icon icon-location-wide"></i>
          {layout === "store-locator" && "Use My Location"}
        </button>
      </div>

      {errorMessage && (
        <p className="mt-2 text-center text-[#D12727] blabel">{errorMessage}</p>
      )}
    </>
  );
});

export default StoreFinder;